import React from "react";
import ReactDOM from "react-dom/client";
import { flushSync } from "react-dom";
import "@/index.css";
import App, { preloadRouteChunk } from "@/App";

/**
 * Boot. Two situations:
 *
 *  1. Plain SPA shell (dev server, or a build where the prerender step did not run): #root is empty.
 *     Render normally.
 *  2. Prerendered page (scripts/prerender.js ran): #root already holds the real page and the <head> holds
 *     that page's title/meta/canonical/JSON-LD. The snapshot is a browser-DOM capture, not React server
 *     output, so it carries no Suspense/hydration markers and React 19 cannot hydrate it without
 *     "hydration failed" re-renders. Instead the live app replaces the snapshot in ONE synchronous commit:
 *     the current route's code-split chunk is preloaded first (so nothing suspends and no "Loading..."
 *     flash appears), then the snapshot's head tags are dropped in the same tick the app renders its own
 *     (react-helmet-async re-adds them), so the document never ends up with two titles or descriptions.
 */
function dropPrerenderedHead() {
  document
    .querySelectorAll("[data-rc-prerender], [data-rc-template]")
    .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
}

async function boot() {
  const container = document.getElementById("root");
  const root = ReactDOM.createRoot(container);
  const tree = (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  if (container.hasChildNodes()) {
    await preloadRouteChunk(window.location.pathname);
    flushSync(() => {
      dropPrerenderedHead();
      root.render(tree);
    });
  } else {
    dropPrerenderedHead(); // template title/description (data-rc-template), replaced by each page's <Seo>
    root.render(tree);
  }
}

boot();
